<template>
    <component :is="elementProperties.is" v-bind="elementProperties" tabindex="0" :aria-label="typeText + ': ' + numberTextAccessible">
        <slot v-bind="{number: numberText}">
            <span aria-hidden="true">
                <i v-if="showIcon" class="material-icons md-18" aria-hidden="true" role="img">{{typeIcon}}</i>
                {{numberText}}
                <template v-if="description">({{description}})</template>
            </span>
        </slot>
    </component>
</template>

<script setup lang="ts">
    import {phoneNumberTypes, phoneNumberTypesDisplay} from '@/library/intranet/phoneNumber';
    import {parseReadableNumbers} from '@/utils/accessibility';
    import {useStore} from 'vuex';
    import {computed} from 'vue';

    const props = withDefaults(
        defineProps<{
            number: string;
            prefix?: string;
            description?: Nullable<string>;
            forceLink?: boolean;
            useAdditionalPrefix?: boolean;
            type?: number;
            showIcon?: boolean;
        }>(),
        {
            prefix: '',
            description: null,
            forceLink: false,
            useAdditionalPrefix: true,
            type: 1,
            showIcon: false,
        },
    );

    const store = useStore();

    const numberText = computed(() =>
    {
        return props.prefix ? '(' + props.prefix + ') ' + props.number : props.number;
    });

    const numberTextAccessible = computed(() =>
    {
        return parseReadableNumbers(numberText.value);
    });

    const elementProperties = computed(() =>
    {
        const settings: PhoneSettings = store.state.layoutData.phone_settings;

        if (
            (!props.forceLink && !settings.telephone_link_active) ||
            phoneNumberTypes.fax === props.type
        )
        {
            return {
                is: 'span',
            };
        }

        let leadingZeroPrefix = '';

        if (
            settings.prefix_with_additional_prefix &&
            settings.external_telephone_number_length <= (props.prefix + props.number).length &&
            props.useAdditionalPrefix
        )
        {
            leadingZeroPrefix = settings.prefix_for_external_number;
        }

        return {
            is: 'a',
            href: 'tel:' + leadingZeroPrefix + props.prefix + props.number,
            title: numberText.value + ' anrufen',
        };
    });

    const typeText = computed(() =>
    {
        const text = phoneNumberTypesDisplay[props.type]?.label || 'Telefon';

        return text + 'nummer';
    });

    const typeIcon = computed(() =>
    {
        return phoneNumberTypesDisplay[props.type]?.icon || phoneNumberTypesDisplay[1].icon;
    });
</script>